import { useEffect, useRef, useState } from 'react'
import { Button } from 'antd'
import { ClearOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

// SignaturePad is a dependency-free canvas the customer draws their
// signature into (mouse or touch via pointer events). onChange fires the
// PNG data-URL while drawing, or null when cleared/empty. The canvas is filled
// white (opaque) so the exported PNG has no alpha and embeds cleanly in PDFs.
export default function SignaturePad({ onChange, height = 160 }: { onChange: (dataUrl: string | null) => void; height?: number }) {
  const { t } = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const dirty = useRef(false)
  const [empty, setEmpty] = useState(true)

  const ctx = () => canvasRef.current?.getContext('2d') ?? null
  const fillWhite = () => {
    const c = ctx(); const cv = canvasRef.current
    if (c && cv) { c.fillStyle = '#FFFFFF'; c.fillRect(0, 0, cv.width, cv.height) }
  }
  useEffect(fillWhite, [])

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const cv = canvasRef.current!
    const rect = cv.getBoundingClientRect()
    // Canvas is CSS-scaled (width:100%) — map pointer coords into the canvas'
    // internal pixel space, otherwise strokes are offset/skewed.
    return { x: (e.clientX - rect.left) * (cv.width / rect.width), y: (e.clientY - rect.top) * (cv.height / rect.height) }
  }

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    const c = ctx(); if (!c) return
    drawing.current = true
    canvasRef.current!.setPointerCapture(e.pointerId)
    const p = pos(e)
    c.lineWidth = 2.2
    c.lineCap = 'round'
    c.strokeStyle = '#0f2540'
    c.beginPath()
    c.moveTo(p.x, p.y)
  }
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const c = ctx(); if (!c) return
    const p = pos(e)
    c.lineTo(p.x, p.y)
    c.stroke()
    if (!dirty.current) { dirty.current = true; setEmpty(false) }
  }
  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    if (dirty.current && canvasRef.current) onChange(canvasRef.current.toDataURL('image/png'))
  }

  const clear = () => {
    fillWhite()
    dirty.current = false
    setEmpty(true)
    onChange(null)
  }

  return (
    <div>
      <div className="signature-pad-box">
        <canvas
          ref={canvasRef}
          width={520}
          height={height}
          className="signature-pad-canvas"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        {empty && <span className="signature-pad-hint">{t('sig.hint', 'Hier mit Maus oder Finger unterschreiben')}</span>}
      </div>
      <div className="mt-4">
        <Button size="small" icon={<ClearOutlined />} onClick={clear} disabled={empty}>
          {t('sig.clear', 'Löschen')}
        </Button>
      </div>
    </div>
  )
}
